import { html } from "../../render";
import { attr, cn, spreadAttrs } from "../../utils";
import { LpIcon } from "../icon";
const rootManagedAttrs = ["data-lp-component", "class", "data-open"];
const panelManagedAttrs = ["data-lp-part", "class"];
const triggerManagedAttrs = ["data-lp-part", "class", "aria-label"];
const inputManagedAttrs = ["class", "type", "placeholder", "aria-label", "disabled"];
const buttonManagedAttrs = ["class", "type", "aria-label", "disabled"];
const rangeHeaderManagedAttrs = ["class"];
const calendarToolbarManagedAttrs = ["class"];
const monthYearPanelManagedAttrs = ["class"];
const calendarGridManagedAttrs = ["class"];
export function LpDatePickerTrigger({ startLabel = "Start date", endLabel = "End date", startPlaceholder = "YYYY-MM-DD", endPlaceholder = "YYYY-MM-DD", startInputAttributes, endInputAttributes, toggleAttributes, clearAttributes, disabled = false, className, attributes, }) {
    return html `
        <div
            data-lp-part="trigger"
            class="${cn("lp-date-picker_trigger", className, attributes?.class)}"
            aria-label="Date range"
            ${spreadAttrs(attributes, { omit: triggerManagedAttrs })}
        >
            <span class="lp-date-picker_trigger-icon" aria-hidden="true">
                ${LpIcon({ name: "calendar", size: 14 })}
            </span>
            <label class="lp-date-picker_field">
                ${startLabel ? html `<span class="lp-date-picker_field-label">${startLabel}</span>` : ""}
                <input
                    class="${cn("lp-date-picker_input", startInputAttributes?.class)}"
                    type="text"
                    placeholder="${startPlaceholder}"
                    aria-label="Start date"
                    ${attr("disabled", disabled)}
                    ${spreadAttrs(startInputAttributes, { omit: inputManagedAttrs })}
                >
            </label>
            <span class="lp-date-picker_separator" aria-hidden="true">—</span>
            <label class="lp-date-picker_field">
                ${endLabel ? html `<span class="lp-date-picker_field-label">${endLabel}</span>` : ""}
                <input
                    class="${cn("lp-date-picker_input", endInputAttributes?.class)}"
                    type="text"
                    placeholder="${endPlaceholder}"
                    aria-label="End date"
                    ${attr("disabled", disabled)}
                    ${spreadAttrs(endInputAttributes, { omit: inputManagedAttrs })}
                >
            </label>
            <button
                class="${cn("lp-date-picker_icon-button", toggleAttributes?.class)}"
                type="button"
                aria-label="Open date picker"
                ${attr("disabled", disabled)}
                ${spreadAttrs(toggleAttributes, { omit: buttonManagedAttrs })}
            >
                ${LpIcon({ name: "caret-down", size: 14 })}
            </button>
            ${clearAttributes ? html `
                <button
                    class="${cn("lp-date-picker_clear-button", clearAttributes?.class)}"
                    type="button"
                    aria-label="Clear date range"
                    ${attr("disabled", disabled)}
                    ${spreadAttrs(clearAttributes, { omit: buttonManagedAttrs })}
                >
                    ${LpIcon({ name: "x", size: 14 })}
                </button>
            ` : ""}
        </div>
    `;
}
export function LpDatePickerRangeHeader({ startAttributes, endAttributes, separator = "—", className, attributes, } = {}) {
    return html `
        <div
            class="${cn("time-period-header", className, attributes?.class)}"
            ${spreadAttrs(attributes, { omit: rangeHeaderManagedAttrs })}
        >
            <div class="time-period-header__range">
                <span class="${cn("time-period-header__date", startAttributes?.class)}" ${spreadAttrs(startAttributes, { omit: ["class"] })}></span>
                <span class="time-period-header__sep" aria-hidden="true">${separator}</span>
                <span class="${cn("time-period-header__date", endAttributes?.class)}" ${spreadAttrs(endAttributes, { omit: ["class"] })}></span>
            </div>
        </div>
    `;
}
export function LpDatePickerCalendarToolbar({ previousAttributes, headingButtonAttributes, headingLabelAttributes, nextAttributes, className, attributes, } = {}) {
    return html `
        <div
            class="${cn("lp-date-picker_calendar-toolbar", className, attributes?.class)}"
            ${spreadAttrs(attributes, { omit: calendarToolbarManagedAttrs })}
        >
            <button
                type="button"
                class="${cn("lp-date-picker_calendar-nav", previousAttributes?.class)}"
                aria-label="Previous month"
                ${spreadAttrs(previousAttributes, { omit: buttonManagedAttrs })}
            >
                <span aria-hidden="true">‹</span>
            </button>
            <div class="lp-date-picker_calendar-heading">
                <button
                    type="button"
                    class="${cn("lp-date-picker_calendar-heading-button", headingButtonAttributes?.class)}"
                    ${spreadAttrs(headingButtonAttributes, { omit: ["class", "type"] })}
                >
                    <span ${spreadAttrs(headingLabelAttributes)}></span>
                </button>
            </div>
            <button
                type="button"
                class="${cn("lp-date-picker_calendar-nav", nextAttributes?.class)}"
                aria-label="Next month"
                ${spreadAttrs(nextAttributes, { omit: buttonManagedAttrs })}
            >
                <span aria-hidden="true">›</span>
            </button>
        </div>
    `;
}
export function LpDatePickerMonthYearPanel({ attributes, monthSectionAttributes, yearSectionAttributes, monthsBinding = "calendarMonths", yearsBinding = "calendarYears", monthButtonAttributes, yearButtonAttributes, } = {}) {
    return html `
        <div
            class="${cn("lp-date-picker_month-year-panel", attributes?.class)}"
            ${spreadAttrs(attributes, { omit: monthYearPanelManagedAttrs })}
        >
            <div class="lp-date-picker_month-year-section" ${spreadAttrs(monthSectionAttributes)}>
                <span class="lp-date-picker_month-year-label">Select month</span>
                <div class="lp-date-picker_month-grid" data-bind="foreach: ${monthsBinding}">
                    <button
                        type="button"
                        class="${cn("lp-date-picker_month-option", monthButtonAttributes?.class)}"
                        ${spreadAttrs(monthButtonAttributes, { omit: buttonManagedAttrs })}
                    ></button>
                </div>
            </div>
            <div class="lp-date-picker_month-year-section" ${spreadAttrs(yearSectionAttributes)}>
                <span class="lp-date-picker_month-year-label">Select year</span>
                <div class="lp-date-picker_year-grid" data-bind="foreach: ${yearsBinding}">
                    <button
                        type="button"
                        class="${cn("lp-date-picker_year-option", yearButtonAttributes?.class)}"
                        ${spreadAttrs(yearButtonAttributes, { omit: buttonManagedAttrs })}
                    ></button>
                </div>
            </div>
        </div>
    `;
}
export function LpDatePickerCalendarGrid({ attributes, sectionAttributes, weekdaysBinding = "calendarWeekdays", daysBinding = "calendarStartDays", dayButtonAttributes, } = {}) {
    return html `
        <div
            class="${cn("time-period-calendars", attributes?.class)}"
            ${spreadAttrs(attributes, { omit: calendarGridManagedAttrs })}
        >
            <section class="time-period-calendar" aria-label="Start month" ${spreadAttrs(sectionAttributes)}>
                <div class="lp-date-picker_weekdays" data-bind="foreach: ${weekdaysBinding}">
                    <span data-bind="text: $data"></span>
                </div>
                <div class="lp-date-picker_days" data-bind="foreach: ${daysBinding}">
                    <button
                        type="button"
                        class="${cn("lp-date-picker_day", dayButtonAttributes?.class)}"
                        ${spreadAttrs(dayButtonAttributes, { omit: buttonManagedAttrs })}
                    ></button>
                </div>
            </section>
        </div>
    `;
}
export function LpDatePicker({ trigger, panel, open = false, className, panelClassName, attributes, panelAttributes, }) {
    return html `
        <div
            data-lp-component="DatePicker"
            class="${cn("lp-date-picker", "w-time-period", className, attributes?.class)}"
            ${attr("data-open", open)}
            ${spreadAttrs(attributes, { omit: rootManagedAttrs })}
        >
            <div class="lp-date-picker_dropdown dropdown">
                ${trigger}
                <div
                    data-lp-part="panel"
                    class="${cn("lp-date-picker_panel", "dropdown-period", "is-visible", panelClassName, panelAttributes?.class)}"
                    ${spreadAttrs(panelAttributes, { omit: panelManagedAttrs })}
                >
                    ${panel}
                </div>
            </div>
        </div>
    `;
}
